import { createFileRoute } from "@tanstack/react-router";
import { BASE_URL, SOCIAL_IMAGE_URL, abs } from "@/lib/seo/constants";
import { aeoGeo, faqPage } from "@/lib/seo/schema";
import { Button } from "@/components/site/Button";
import { Section, EyebrowHeading } from "@/components/site/Section";
import { KineticHeading } from "@/components/site/wow/KineticHeading";
import { Reveal, RevealStagger, RevealItem } from "@/components/site/wow/Reveal";
import { GrainOverlay } from "@/components/site/wow/GrainOverlay";
import { CareerForm } from "@/components/site/CareerForm";

const PATH = "/en/careers";
const TITLE = "Careers — film talent and partner photographers | Fenny";
const DESCRIPTION =
  "Apply for film roles or become a Fenny Partner Photographer. We are building a nationwide network of Sweden-based photographers who represent Fenny on location.";
const CANONICAL = abs(PATH);
const DATE_MODIFIED = "2026-10-02";

export const Route = createFileRoute("/en_/careers")({
  head: () => ({
    title: TITLE,
    meta: [
      { name: "description", content: DESCRIPTION },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESCRIPTION },
      { property: "og:type", content: "website" },
      { property: "og:url", content: CANONICAL },
      { property: "og:image", content: SOCIAL_IMAGE_URL },
      { property: "og:locale", content: "en_US" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: TITLE },
      { name: "twitter:description", content: DESCRIPTION },
      { name: "twitter:image", content: SOCIAL_IMAGE_URL },
    ],
    links: [
      { rel: "canonical", href: CANONICAL },
    ],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify(aeoGeo({
          path: PATH,
          name: "Careers at Fenny",
          description: DESCRIPTION,
          question: "Is Fenny looking for film talent and partner photographers?",
          answer:
            "Yes — Fenny accepts spontaneous film applications and is looking for Sweden-based partner photographers. Photographers represent Fenny on location while Fenny handles sales, briefs, project management, agreements and invoicing.",
          dateModified: DATE_MODIFIED,
          inLanguage: "en-US",
        })),
      },
      { type: "application/ld+json", children: JSON.stringify(faqPage(FAQ)) },
    ],
  }),
  component: CareersPage,
});

function CareersPage() {
  return (
    <main className="relative overflow-hidden">
      <GrainOverlay />

      <section className="relative flex min-h-[70vh] flex-col items-center justify-center px-6 py-24 text-center">
        <div className="mx-auto max-w-4xl">
          <Reveal>
            <p className="mb-4 text-sm font-medium uppercase tracking-widest text-mint">Careers</p>
          </Reveal>
          <KineticHeading
            text="We are always looking for talent — not only when a seat is open"
            as="h1"
            className="text-4xl font-semibold leading-[1.1] tracking-tight md:text-6xl"
            highlight={{ indices: [3, 4, 5], className: "text-mint" }}
          />
          <Reveal delay={0.35}>
            <p className="lede mx-auto mt-6 max-w-2xl text-lg text-muted-foreground md:text-xl">
              Our clients value proactivity, boundless creativity and people who take initiative. Whether you are a scriptwriter, director, filmmaker, editor, 3D artist, casting specialist or freelancer — we want to meet you.
            </p>
          </Reveal>
          <Reveal delay={0.55}>
            <div className="mt-10 flex flex-col items-center gap-4 sm:flex-row sm:justify-center">
              <Button href="#apply" variant="mint" arrow size="lg">
                Apply now
              </Button>
              <Button href="mailto:hey@fenny.se" variant="outline" size="lg">
                Email us directly
              </Button>
            </div>
          </Reveal>
        </div>
      </section>

      <Section>
        <div className="mx-auto max-w-3xl rounded-3xl border border-white/10 bg-surface/40 p-8 md:p-12">
          <p className="text-lg leading-relaxed" data-quick-answer>
            Fenny is a film production agency in Stockholm working on both own productions and client commissions across Sweden. We accept spontaneous applications and freelance assignments continuously — whether you prefer on-site, remote or project-based work.
          </p>
        </div>
      </Section>

      <Section>
        <EyebrowHeading
          eyebrow="Who we are looking for"
          title="Roles that are always interesting"
          lede="Below are the profiles we often match to our clients' needs. Spontaneous applications are always welcome."
        />
        <RevealStagger className="mt-12 grid gap-4 sm:grid-cols-2 lg:grid-cols-3" stagger={0.06}>
          {ROLES.map((r) => (
            <RevealItem key={r.label}>
              <div className="h-full rounded-2xl border border-white/10 bg-surface p-6 transition-colors hover:border-mint/30">
                <div className="mb-3 text-2xl">{r.icon}</div>
                <h3 className="text-lg font-semibold">{r.label}</h3>
                <p className="mt-2 text-sm text-muted-foreground">{r.body}</p>
              </div>
            </RevealItem>
          ))}
        </RevealStagger>
      </Section>

      <Section className="bg-surface/30">
        <div className="grid gap-10 md:grid-cols-[1.1fr_0.9fr] md:items-start">
          <EyebrowHeading
            eyebrow="Fenny Partner Photographer"
            title="You shoot. We handle the rest."
            lede="We are building a photography department with Sweden-based partner photographers who act as Fenny on location. Each photographer keeps their unique style and visual expression — the client chooses a direction and we match the right assignment with the right photographer."
          />
          <RevealStagger className="grid gap-4" stagger={0.06}>
            {PARTNER_PHOTOGRAPHER.map((item) => (
              <RevealItem key={item.label}>
                <div className="rounded-2xl border border-white/10 bg-background p-5">
                  <h3 className="font-semibold">{item.label}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{item.body}</p>
                </div>
              </RevealItem>
            ))}
          </RevealStagger>
        </div>
        <Reveal delay={0.2}>
          <div className="mt-8">
            <Button
              id="partner-photographer-cta-en"
              href="#apply"
              variant="mint"
              arrow
              onClick={(event) => {
                event.preventDefault();
                window.dispatchEvent(new Event("fenny:select-partner-photographer"));
                window.history.replaceState(null, "", "#apply");
                window.requestAnimationFrame(() => document.getElementById("apply")?.scrollIntoView({ behavior: "smooth" }));
              }}
            >
              Apply as a partner photographer
            </Button>
          </div>
        </Reveal>
      </Section>

      <Section className="bg-surface/30">
        <div className="grid gap-12 md:grid-cols-2 md:items-center">
          <EyebrowHeading
            eyebrow="Working with Fenny"
            title="Small agency, big mandate"
            lede="We believe in fast decisions, short decision paths and everyone owning their area."
          />
          <RevealStagger className="grid gap-4 sm:grid-cols-2" stagger={0.05}>
            {CULTURE.map((c) => (
              <RevealItem key={c.label}>
                <div className="rounded-2xl border border-white/10 bg-background p-5">
                  <h4 className="font-semibold">{c.label}</h4>
                  <p className="mt-1 text-sm text-muted-foreground">{c.body}</p>
                </div>
              </RevealItem>
            ))}
          </RevealStagger>
        </div>
      </Section>

      <Section>
        <EyebrowHeading
          eyebrow="The process"
          title="From application to collaboration"
          lede="This is what happens when you get in touch."
        />
        <div className="mt-12 grid gap-6 md:grid-cols-5">
          {PROCESS.map((step, i) => (
            <Reveal key={step.label} delay={i * 0.08}>
              <div className="relative rounded-2xl border border-white/10 bg-surface p-5 text-center">
                <div className="mx-auto mb-3 flex h-10 w-10 items-center justify-center rounded-full bg-mint/15 text-sm font-semibold text-mint">
                  {i + 1}
                </div>
                <h4 className="font-semibold">{step.label}</h4>
                <p className="mt-1 text-sm text-muted-foreground">{step.body}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </Section>

      <CareerForm locale="en" location="careers" partnerCtaId="partner-photographer-cta-en" />

      <Section className="bg-surface/30">
        <EyebrowHeading eyebrow="FAQ" title="Careers & collaboration" />
        <div className="mt-10 grid gap-6 md:grid-cols-2">
          {FAQ.map((item) => (
            <Reveal key={item.q}>
              <div className="rounded-2xl border border-white/10 bg-background p-6">
                <h3 className="font-semibold">{item.q}</h3>
                <p className="mt-2 text-sm text-muted-foreground">{item.a}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </Section>

      <Section>
        <div className="mx-auto max-w-3xl rounded-3xl border border-mint/20 bg-mint/5 p-10 text-center">
          <h2 className="text-2xl font-semibold md:text-3xl">Ready to show what you can do?</h2>
          <p className="mt-3 text-muted-foreground">
            Send a spontaneous application or email hey@fenny.se — we read everything.
          </p>
          <div className="mt-8 flex flex-col items-center justify-center gap-4 sm:flex-row">
            <Button href="#apply" variant="mint" arrow size="lg">
              Apply now
            </Button>
            <Button href="mailto:hey@fenny.se" variant="outline" size="lg">
              hey@fenny.se
            </Button>
          </div>
        </div>
      </Section>
    </main>
  );
}

const ROLES = [
  { label: "Spontaneous application", icon: "✨", body: "No perfect role on the list? Reach out anyway — we save every profile." },
  { label: "Freelancer", icon: "🤝", body: "Work project-based with us and our clients, remotely or on-site." },
  { label: "Photographer (Partner)", icon: "📸", body: "Represent Fenny on project-based photography assignments in your region." },
  { label: "Scriptwriter", icon: "✍️", body: "Develop concepts, scripts and stories for ads, documentary and corporate film." },
  { label: "3D artist", icon: "🧊", body: "Motion graphics, product visualisation and environments for film and stills." },
  { label: "Editor", icon: "✂️", body: "Shape rhythm, feeling and structure in both short and long formats." },
  { label: "Director", icon: "🎬", body: "Lead creative teams and capture the right tone between brand and audience." },
  { label: "Casting / talent", icon: "🎭", body: "Find the right people in front of the camera — actors, experts and everyday heroes." },
  { label: "Post-production", icon: "🎛️", body: "Colour, sound, VFX, retouch and delivery for all channels." },
  { label: "Filmmaker / DOP", icon: "📷", body: "Shoot commercial content for web, social media and broadcast." },
];

const PARTNER_PHOTOGRAPHER = [
  {
    label: "You focus on photography",
    body: "Fenny handles sales, client communication, the brief, project management, agreements and invoicing. You arrive prepared and deliver to the agreed brief and quality standard.",
  },
  {
    label: "Your style guides the match",
    body: "We do not mould photographers into one look. The client chooses the visual direction, and Fenny matches the assignment with the partner photographer whose personal approach, niche and aesthetic fit best.",
  },
  {
    label: "You represent Fenny",
    body: "With the client, you act as part of Fenny rather than as an outside agency profile. A professional experience and dependable delivery matter as much as the images.",
  },
  {
    label: "Assignments across Sweden",
    body: "We are looking for photographers in Stockholm, Gothenburg, Malmö and every other region to build a nationwide network close to our clients.",
  },
];

const CULTURE = [
  { label: "Proactivity is rewarded", body: "We value people who see what is needed before anyone asks." },
  { label: "Short decision paths", body: "No long hierarchies — one conversation can lead to an assignment the same week." },
  { label: "Creative freedom", body: "You own your part and can propose solutions, not just execute briefs." },
  { label: "Close to the client", body: "We work tightly with brands and learn their worlds for real." },
];

const PROCESS = [
  { label: "Apply", body: "Fill in the form with role, link and a short message." },
  { label: "We read", body: "Every application is reviewed by the production team within one week." },
  { label: "Coffee chat", body: "If there is a match, we invite you to a short, relaxed conversation." },
  { label: "Test assignment", body: "A small assignment shows how we collaborate in practice." },
  { label: "Collaborate", body: "When the fit is mutual, you become part of our network." },
];

const FAQ = [
  {
    q: "How do assignments work for a Fenny Partner Photographer?",
    a: "The collaboration is project-based. The client chooses the visual direction, and we match the assignment with the partner photographer whose personal style, region, experience and availability fit best. You accept each assignment individually and deliver the shoot to the brief.",
  },
  {
    q: "Do I need to adapt my style to a Fenny template?",
    a: "No. Your personal aesthetic is an important part of the match. We want photographers with different approaches — such as documentary, editorial, minimal or warm and lifestyle-led — so clients can choose the style that fits their assignment.",
  },
  {
    q: "Who do I represent on location with the client?",
    a: "You represent Fenny and act as part of our team during the assignment. Fenny owns the client relationship and handles sales, client communication, project management and agreements before, during and after the shoot.",
  },
  {
    q: "How do fees and invoicing work?",
    a: "You need your own company and Swedish F-tax approval so you can invoice Fenny. Fenny invoices the client, and you invoice Fenny the fee agreed for the assignment.",
  },
  {
    q: "Can I send a spontaneous application?",
    a: "Absolutely — we always accept spontaneous applications and save your profile for upcoming assignments.",
  },
  {
    q: "Are you looking for freelancers or employees?",
    a: "Both. Many collaborations start as freelance assignments and grow over time.",
  },
  {
    q: "Where is Fenny based?",
    a: "Our headquarters are in Stockholm, but we work with clients and talent across Sweden.",
  },
  {
    q: "What should I include?",
    a: "A link to your portfolio, showreel or previous projects — and a short personal message.",
  },
];
